import { useMemo, useState } from "react";
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import { parseItemDate } from "../../utils/dateUtils";

const dateKey = (date, range) => {
  const d = new Date(date);
  if (range === "year") {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  }
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
};

const parseSleepMinutes = (duration, item) => {
  if (!duration && item?.startTime && item?.endTime) {
    const start = new Date(`1970-01-01T${item.startTime}`);
    const end = new Date(`1970-01-01T${item.endTime}`);
    if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
      const mins = (end.getTime() - start.getTime()) / 60000;
      return mins > 0 ? mins : 0;
    }
  }
  if (!duration) return 0;
  const match = duration.match(/(\d+)h\s*(\d+)m/);
  if (match) return parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
  const minutesMatch = duration.match(/(\d+)m/);
  if (minutesMatch) return parseInt(minutesMatch[1], 10);
  return 0;
};

const getRangeStart = (range) => {
  const now = new Date();
  if (range === "month") {
    const start = new Date(now);
    start.setDate(now.getDate() - 29);
    return new Date(start.setHours(0, 0, 0, 0));
  }
  if (range === "year") {
    const start = new Date(now);
    start.setMonth(now.getMonth() - 11);
    start.setDate(1);
    return new Date(start.setHours(0, 0, 0, 0));
  }
  const start = new Date(now);
  start.setDate(now.getDate() - 6);
  return new Date(start.setHours(0, 0, 0, 0));
};

const buildKeys = (range) => {
  const keys = [];
  const now = new Date();
  if (range === "year") {
    for (let i = 11; i >= 0; i -= 1) {
      const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
      keys.push(dateKey(monthDate, range));
    }
    return keys;
  }
  const start = getRangeStart(range);
  const dayCount = range === "month" ? 30 : 7;
  for (let i = 0; i < dayCount; i += 1) {
    const date = new Date(start);
    date.setDate(start.getDate() + i);
    keys.push(dateKey(date, range));
  }
  return keys;
};

const formatLabel = (key, range) => {
  if (range === "year") {
    const [year, month] = key.split("-");
    return `${month}/${year.slice(2)}`;
  }
  const [, month, day] = key.split("-");
  return `${day}/${month}`;
};

const aggregateData = (items, range, field, reducer) => {
  const buckets = buildKeys(range).reduce((acc, key) => {
    acc[key] = 0;
    return acc;
  }, {});
  items.forEach((item) => {
    const parsed = parseItemDate(item);
    if (!parsed) return;
    const key = dateKey(parsed, range);
    if (key in buckets) buckets[key] += reducer(item, field);
  });
  return buckets;
};

const buildChartData = ({ range, bottleData, waterData, sleepData, solidFoodData, growthHealthData }) => {
  const bottleBuckets  = aggregateData(bottleData,      range, "amount",   (item) => parseFloat(item.amount || 0));
  const waterBuckets   = aggregateData(waterData,       range, "amount",   (item) => parseFloat(item.amount || 0));
  const sleepBuckets   = aggregateData(sleepData,       range, "duration", (item) => parseSleepMinutes(item.duration, item));
  const solidsBuckets  = aggregateData(solidFoodData,   range, null,       () => 1);
  const growthBuckets  = aggregateData(growthHealthData, range, "weight",  (item) => (item.weight ? parseFloat(item.weight || 0) : 0));

  return buildKeys(range).map((key) => ({
    period:       formatLabel(key, range),
    bottleOz:     Math.round((bottleBuckets[key]  || 0) * 10) / 10,
    waterOz:      Math.round((waterBuckets[key]   || 0) * 10) / 10,
    sleepHours:   Math.round(((sleepBuckets[key]  || 0) / 60) * 10) / 10,
    solidCount:   solidsBuckets[key] || 0,
    growthWeight: Math.round((growthBuckets[key]  || 0) * 10) / 10,
  }));
};

const getRangeBounds = (range) => {
  const now = new Date();
  if (range === "year") {
    const start = new Date(now.getFullYear(), now.getMonth() - 11, 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
    return { start, end };
  }
  const length = range === "month" ? 30 : 7;
  const finish = new Date(now);
  finish.setHours(23, 59, 59, 999);
  const start = new Date(now);
  start.setDate(now.getDate() - (length - 1));
  start.setHours(0, 0, 0, 0);
  return { start, end: finish };
};

const computeRangeSummary = ({ items, range, field, reducer, between }) => {
  const now = new Date();
  const compareStart = new Date(now);
  const compareEnd = new Date(now);
  if (range === "year") {
    compareStart.setMonth(now.getMonth() - 12);
    compareStart.setDate(1);
    compareEnd.setMonth(now.getMonth() - 1);
    compareEnd.setDate(new Date(compareEnd.getFullYear(), compareEnd.getMonth() + 1, 0).getDate());
  } else {
    const length = range === "month" ? 30 : 7;
    compareStart.setDate(now.getDate() - length * 2 + 1);
    compareEnd.setDate(now.getDate() - length);
  }
  const value = items.reduce((sum, item) => {
    const date = parseItemDate(item);
    if (!date || date < between.start || date > between.end) return sum;
    return sum + reducer(item, field);
  }, 0);
  const prevValue = items.reduce((sum, item) => {
    const date = parseItemDate(item);
    if (!date || date < compareStart || date > compareEnd) return sum;
    return sum + reducer(item, field);
  }, 0);
  return { value, prevValue };
};

// Tooltip styles shared across both charts
const tooltipStyle = {
  borderRadius: "0.5rem",
  border: "1px solid var(--border-light)",
  fontSize: "0.82rem",
  boxShadow: "var(--shadow-md)",
};

const TrendBadge = ({ current, previous }) => {
  if (!previous || previous === 0) return null;
  const pct = Math.round(((current - previous) / Math.abs(previous)) * 100);
  const up = pct >= 0;
  return (
    <span
      style={{
        display: "inline-block",
        fontSize: "0.72rem",
        fontWeight: "600",
        color: up ? "#2e7d32" : "#c62828",
        background: up ? "rgba(200,230,201,0.5)" : "rgba(255,205,210,0.5)",
        padding: "0.1rem 0.45rem",
        borderRadius: "50px",
        marginTop: "0.3rem",
      }}
    >
      {up ? "↑" : "↓"} {Math.abs(pct)}% vs prev
    </span>
  );
};

const TrendCharts = ({ bottleData, waterData, sleepData, solidFoodData, growthHealthData }) => {
  const [range, setRange] = useState("week");

  const chartData = useMemo(
    () => buildChartData({ range, bottleData, waterData, sleepData, solidFoodData, growthHealthData }),
    [range, bottleData, waterData, sleepData, solidFoodData, growthHealthData]
  );

  const bounds = useMemo(() => getRangeBounds(range), [range]);

  const bottleSummary = useMemo(() => computeRangeSummary({
    items: bottleData, range, field: "amount",
    reducer: (item) => parseFloat(item.amount || 0), between: bounds,
  }), [bottleData, bounds, range]);

  const waterSummary = useMemo(() => computeRangeSummary({
    items: waterData, range, field: "amount",
    reducer: (item) => parseFloat(item.amount || 0), between: bounds,
  }), [waterData, bounds, range]);

  const sleepSummary = useMemo(() => computeRangeSummary({
    items: sleepData, range, field: "duration",
    reducer: (item) => parseSleepMinutes(item.duration, item), between: bounds,
  }), [sleepData, bounds, range]);

  const solidSummary = useMemo(() => computeRangeSummary({
    items: solidFoodData, range, reducer: () => 1, field: null, between: bounds,
  }), [solidFoodData, bounds, range]);

  const rangeOptions = [
    { value: "week",  label: "7d"   },
    { value: "month", label: "30d"  },
    { value: "year",  label: "12mo" },
  ];

  return (
    <div className="card" id="trend-charts">
      <div className="card-header">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "0.75rem",
          }}
        >
          <h5 className="mb-0">📈 Trends &amp; Comparisons</h5>
          <div className="btn-group btn-group-sm" role="group" aria-label="Time range">
            {rangeOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`btn ${range === opt.value ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setRange(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="card-body">
        <>
          {/* Summary cards — reuse the same grid + card class as the daily summary */}
          <div className="summary-cards-grid mb-4">
              <div className="summary-card">
                <div style={{ fontSize: "1.75rem" }}>🍼</div>
                <p className="summary-card-label">Bottle</p>
                <div className="summary-card-value" style={{ fontSize: "1.4rem" }}>
                  {Math.round(bottleSummary.value * 10) / 10} oz
                </div>
                <TrendBadge current={bottleSummary.value} previous={bottleSummary.prevValue} />
              </div>

              <div className="summary-card">
                <div style={{ fontSize: "1.75rem" }}>💧</div>
                <p className="summary-card-label">Water</p>
                <div className="summary-card-value" style={{ fontSize: "1.4rem" }}>
                  {Math.round(waterSummary.value * 10) / 10} oz
                </div>
                <TrendBadge current={waterSummary.value} previous={waterSummary.prevValue} />
              </div>

              <div className="summary-card">
                <div style={{ fontSize: "1.75rem" }}>😴</div>
                <p className="summary-card-label">Sleep</p>
                <div className="summary-card-value" style={{ fontSize: "1.4rem" }}>
                  {Math.round((sleepSummary.value / 60) * 10) / 10} hrs
                </div>
                <TrendBadge current={sleepSummary.value} previous={sleepSummary.prevValue} />
              </div>

              <div className="summary-card">
                <div style={{ fontSize: "1.75rem" }}>🥘</div>
                <p className="summary-card-label">Solid Meals</p>
                <div className="summary-card-value" style={{ fontSize: "1.4rem" }}>
                  {solidSummary.value}
                </div>
                <TrendBadge current={solidSummary.value} previous={solidSummary.prevValue} />
              </div>
            </div>

            {/* Charts */}
            <div className="row g-4">
              <div className="col-lg-8">
                <div
                  style={{
                    border: "1px solid var(--border-light)",
                    borderRadius: "var(--radius-md)",
                    padding: "1rem",
                  }}
                >
                  <h6
                    style={{
                      color: "var(--text-dark)",
                      fontWeight: "600",
                      marginBottom: "1rem",
                    }}
                  >
                    Feeding + Sleep Trend
                  </h6>
                  <div className="chart-container">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
                        <XAxis
                          dataKey="period"
                          tick={{ fontSize: 11, fill: "var(--text-light)" }}
                        />
                        <YAxis tick={{ fontSize: 11, fill: "var(--text-light)" }} />
                        <Tooltip contentStyle={tooltipStyle} />
                        <Legend wrapperStyle={{ fontSize: "0.8rem" }} />
                        <Line
                          type="monotone"
                          dataKey="bottleOz"
                          stroke="#FF85C0"
                          name="Bottle oz"
                          strokeWidth={2}
                          dot={false}
                          activeDot={{ r: 4 }}
                        />
                        <Line
                          type="monotone"
                          dataKey="waterOz"
                          stroke="#4DD0E1"
                          name="Water oz"
                          strokeWidth={2}
                          dot={false}
                          activeDot={{ r: 4 }}
                        />
                        <Line
                          type="monotone"
                          dataKey="sleepHours"
                          stroke="#CE93D8"
                          name="Sleep hrs"
                          strokeWidth={2}
                          dot={false}
                          activeDot={{ r: 4 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              <div className="col-lg-4">
                <div
                  style={{
                    border: "1px solid var(--border-light)",
                    borderRadius: "var(--radius-md)",
                    padding: "1rem",
                  }}
                >
                  <h6
                    style={{
                      color: "var(--text-dark)",
                      fontWeight: "600",
                      marginBottom: "1rem",
                    }}
                  >
                    Food &amp; Growth
                  </h6>
                  <div className="chart-container-sm">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
                        <XAxis
                          dataKey="period"
                          tick={{ fontSize: 11, fill: "var(--text-light)" }}
                        />
                        <YAxis tick={{ fontSize: 11, fill: "var(--text-light)" }} />
                        <Tooltip contentStyle={tooltipStyle} />
                        <Legend wrapperStyle={{ fontSize: "0.8rem" }} />
                        <Bar dataKey="solidCount"   name="Solid foods" fill="#66BB6A" radius={[3, 3, 0, 0]} />
                        <Bar dataKey="growthWeight" name="Avg weight"  fill="#FF8A65" radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <p
                    style={{
                      color: "var(--text-light)",
                      fontSize: "0.78rem",
                      marginTop: "0.75rem",
                      marginBottom: 0,
                    }}
                  >
                    Growth data is averaged over the selected range.
                  </p>
                </div>
              </div>
            </div>
        </>
      </div>
    </div>
  );
};

export default TrendCharts;
